import { IconeTrait } from '@/components/ui/icone-trait';
import { renderLienRouteur } from '@/components/ui/lien-routeur';
import { Button, Card, Icon, LoadingRing } from '@pms-digital/design-system';
import type { NoyauCode, ResumeTraduction } from '@rosetta/shared';
import type { MouseEvent } from 'react';
import { formaterDate } from './formater-date';
import { IconeHistorique } from './icone-historique';
import { PastillesNoyaux } from './pastilles-noyaux';

interface CarteEnCoursProps {
  traduction: ResumeTraduction;
  /** The current instant, to word the date ("aujourd'hui"); injectable for the tests. */
  maintenant?: Date;
  /** An interruption of this translation is running: the button is locked. */
  interruptionEnCours?: boolean;
  /** Asks to interrupt (the screen then asks for the confirmation, W2). */
  onInterrompre: (evenement: MouseEvent<HTMLButtonElement>) => void;
  /** A blocked translation (#13): opens the input screen filled with it. */
  onRelancer?: () => void;
  /**
   * Noyaux being added to a text already translated (#11, H9): their pills say "en cours", the
   * others are the noyaux already translated.
   */
  noyauxAjoutes?: readonly NoyauCode[];
  /** The running translation of each added noyau (#36): its pill leads to the waiting screen of that one. */
  lancementsAjoutes?: Readonly<Partial<Record<NoyauCode, string>>>;
  /** The translation each noyau already translated opens (#36): its pill is a link to that result. */
  lancementsTraduits?: Readonly<Partial<Record<NoyauCode, string>>>;
  /**
   * The translation the title opens, while noyaux are added (H9): the result of what is already
   * translated, rather than the waiting screen of the add.
   */
  idResultat?: string;
}

/**
 * H6 (#7): the card of a translation that is still running, the `Card` of the design system. The
 * title (a link to the waiting screen W1, stretched over the whole card since #21 by the package,
 * the buttons and the pills are above it) with the date under it, "en cours" and its ring at the
 * top right, two lines of the original text, the noyaux, and "interrompre" in the foot
 * (`stop_circle`, `gray-700`).
 *
 * H8 (#13), the same card in its "anomalie" state, for a blocked translation (one of its steps
 * failed, Tahiti will not end it): "bloquée" in `rouge-deep` with the `error` icon instead of the
 * ring, the noyaux, then "relancer" and "interrompre" in the foot. The title opens W1, which says
 * what failed.
 * H9 (#11) is the same card while noyaux are added to a text already translated: the pills of the
 * added noyaux say "en cours", the title opens the result of what is translated, and
 * "interrompre" stops the add only.
 */
export function CarteEnCours({
  traduction,
  maintenant,
  interruptionEnCours = false,
  onInterrompre,
  onRelancer,
  noyauxAjoutes,
  lancementsAjoutes,
  lancementsTraduits,
  idResultat,
}: CarteEnCoursProps) {
  const { id, titre, texteOrigine, noyaux, creeLe, statut } = traduction;
  const bloquee = statut === 'bloquee';
  const interrompre = (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      aria-disabled={interruptionEnCours}
      onClick={interruptionEnCours ? () => undefined : onInterrompre}
    >
      <IconeTrait name="stop_circle" size={18} />
      {interruptionEnCours ? 'interruption...' : 'interrompre'}
    </Button>
  );

  return (
    <Card
      surface="border"
      label={`${titre ?? 'Sans titre'}, traduction ${bloquee ? 'bloquée' : 'en cours'}`}
      title={
        titre === undefined ? (
          <span className="font-normal text-gray-600 italic">Sans titre</span>
        ) : (
          titre
        )
      }
      titleHref={`/resultat/${idResultat ?? id}`}
      renderLink={renderLienRouteur}
      meta={<time dateTime={creeLe}>{formaterDate(creeLe, maintenant)}</time>}
      trailing={
        bloquee ? (
          <span className="inline-flex items-center gap-1 whitespace-nowrap font-semibold text-caption text-rouge-deep">
            <Icon name="error" size={16} />
            bloquée
          </span>
        ) : (
          <span className="inline-flex items-center gap-2 whitespace-nowrap text-caption text-gray-700">
            <LoadingRing size="sm" />
            en cours
          </span>
        )
      }
      actionsAlign={bloquee ? 'start' : 'end'}
      actions={
        bloquee ? (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onRelancer ?? (() => undefined)}
            >
              <IconeHistorique name="refresh" size={18} />
              relancer
            </Button>
            {interrompre}
          </>
        ) : (
          interrompre
        )
      }
    >
      <div className="flex h-full min-h-28 flex-col gap-3">
        <p className="m-0 line-clamp-2 font-light leading-[1.55]">{texteOrigine}</p>
        <div className="mt-auto flex flex-wrap items-center gap-2">
          <PastillesNoyaux
            noyaux={noyaux}
            {...(!bloquee && noyauxAjoutes !== undefined ? { enCours: noyauxAjoutes } : {})}
            {...(!bloquee && lancementsAjoutes !== undefined
              ? { lancements: lancementsAjoutes }
              : {})}
            {...(!bloquee && lancementsTraduits !== undefined
              ? { traductions: lancementsTraduits }
              : {})}
          />
        </div>
      </div>
    </Card>
  );
}
